import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const POLICY_VERSION = "1.0.0";
const STORAGE_KEY = "cookieConsent";

export interface CookieChoices {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface ConsentData {
  choices: CookieChoices;
  version: string;
  givenAt: string;
  source: "banner" | "modal";
}

interface CookieConsentContextType {
  consent: ConsentData | null;
  showBanner: boolean;
  showModal: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (choices: CookieChoices) => void;
  openModal: () => void;
  closeModal: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const defaultChoices: CookieChoices = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    simulateConsentChange?: (category: keyof CookieChoices, value: boolean) => void;
  }
}

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentData | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Initialize consent on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: ConsentData = JSON.parse(stored);
        // Check if version changed
        if (parsed.version === POLICY_VERSION) {
          setConsent(parsed);
          window.__cookieConsent = parsed.choices;
          updateConsentMode(parsed.choices);
          loadCookieScripts(parsed.choices);
        } else {
          // Version changed, show banner again
          setShowBanner(true);
          initConsentMode();
        }
      } catch (e) {
        setShowBanner(true);
        initConsentMode();
      }
    } else {
      setShowBanner(true);
      initConsentMode();
    }

    // Track if user leaves without choosing
    const handleBeforeUnload = () => {
      if (showBanner && !consent) {
        trackConsentEvent("consent_denied_before_load", defaultChoices, "banner");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Track banner shown
  useEffect(() => {
    if (showBanner && !consent) {
      trackConsentEvent("consent_shown", defaultChoices, "banner");
    }
  }, [showBanner]);

  const initConsentMode = () => {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
        functionality_storage: "granted",
        security_storage: "granted",
      });
    }
  };

  const updateConsentMode = (choices: CookieChoices) => {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: choices.analytics ? "granted" : "denied",
        ad_storage: choices.marketing ? "granted" : "denied",
        ad_user_data: choices.marketing ? "granted" : "denied",
        ad_personalization: choices.marketing ? "granted" : "denied",
        functionality_storage: choices.functional ? "granted" : "denied",
      });
    }
  };

  const saveConsent = async (choices: CookieChoices, source: "banner" | "modal") => {
    const consentData: ConsentData = {
      choices,
      version: POLICY_VERSION,
      givenAt: new Date().toISOString(),
      source,
    };

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));
    setConsent(consentData);
    window.__cookieConsent = choices;

    // Update consent mode
    updateConsentMode(choices);

    // Load scripts
    loadCookieScripts(choices);

    // Save to Lovable Cloud (non-blocking)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from("cookie_logs").insert({
        user_id: user?.id,
        choices: choices as any,
        version: POLICY_VERSION,
        source,
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      // Only log detailed errors in development
      if (import.meta.env.DEV) {
        console.warn("Failed to save consent to Cloud:", error);
      }
    }

    setShowBanner(false);
    setShowModal(false);
  };

  const trackConsentEvent = (eventType: string, choices: CookieChoices, source: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "consent_event",
        type: eventType,
        version: POLICY_VERSION,
        source,
        choices,
        timestamp: Date.now(),
      });
    }
  };

  const acceptAll = () => {
    const allChoices: CookieChoices = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    trackConsentEvent("consent_accepted_all", allChoices, "banner");
    saveConsent(allChoices, "banner");
  };

  const rejectAll = () => {
    trackConsentEvent("consent_rejected_all", defaultChoices, "banner");
    saveConsent(defaultChoices, "banner");
  };

  const savePreferences = (choices: CookieChoices) => {
    trackConsentEvent("consent_saved_preferences", choices, "modal");
    saveConsent(choices, "modal");
  };

  const openModal = () => {
    setShowModal(true);
    setShowBanner(false);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // Test hook
  useEffect(() => {
    window.simulateConsentChange = (category: keyof CookieChoices, value: boolean) => {
      if (consent) {
        const newChoices = { ...consent.choices, [category]: value };
        savePreferences(newChoices);
      }
    };
  }, [consent]);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        showModal,
        acceptAll,
        rejectAll,
        savePreferences,
        openModal,
        closeModal,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return context;
};

// Script loader utility
const loadCookieScripts = (choices: CookieChoices) => {
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-cookie-category]');
  
  scripts.forEach((script) => {
    const category = script.getAttribute("data-cookie-category") as keyof CookieChoices;
    if (choices[category]) {
      const newScript = document.createElement("script");
      newScript.textContent = script.textContent;
      if (script.src) newScript.src = script.src;
      Array.from(script.attributes).forEach((attr) => {
        if (attr.name !== "type" && attr.name !== "data-cookie-category") {
          newScript.setAttribute(attr.name, attr.value);
        }
      });
      script.parentNode?.replaceChild(newScript, script);
    }
  });
};
